// The page of an HTML plan (today's plans/PlanView.tsx, Poulpe-128): the
// plan's structure and words from its body (`PlanModel`), every state, number
// and flag from the store. In order: the overview and its numbers, Progress,
// the phases as folds, the issues listed elsewhere, the decisions in
// force and the annex, folded. A body off the structure is shown as written,
// with what did not fit.
import { useCallback, useEffect, useMemo, useRef, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { Issue } from '../api/types'
import { StateIcon } from '../components/Icons'
import { stateName } from '../vocab'
import { issuePath, PlanHtml, PlanKeysProvider, usePlanKeys } from './html'
import type { PlanModel, PlanPhase } from './model'
import { StageMeter } from './parts'
import { PhaseCard } from './PhaseCard'
import { Progress } from './Progress'
import { anchorOf, openTarget, revealTarget } from './reveal'
import { planFacts, planKpis, type PlanFacts, type PlanKpis } from './state'
import { compact, planWords as w } from './words'
import { sumTokens, useIssueWork } from './work'
import './planview.css'

export interface PlanViewProps {
  /** The plan's id. */
  planId: string
  model: PlanModel
  /** The issues the plan lists, as the store has them. */
  listed: readonly Issue[]
  /** Every issue the store holds: an item's key is looked up here. */
  issues: ReadonlyMap<string, Issue>
  /** The workspace's projects' keys: what a key is read in. */
  names: readonly string[]
  /** Starts a dispatcher on a phase (dispatch.ts); none, and no phase has the action. */
  onDispatch?: (phase: PlanPhase) => void
  offline?: boolean
}

/** The plan's facts, once per change of what they are computed from. */
export function usePlanFacts({ model, listed, issues }: Pick<PlanViewProps, 'model' | 'listed' | 'issues'>): PlanFacts {
  return useMemo(() => planFacts({ model, listed, issues }), [model, listed, issues])
}

export function PlanView(props: PlanViewProps) {
  const { planId, model } = props
  const facts = usePlanFacts(props)
  const kpis = useMemo(() => planKpis(facts), [facts])
  const keys = usePlanKeys(props.issues, props.names)
  const navigate = useNavigate()
  const { hash } = useLocation()
  const root = useRef<HTMLDivElement>(null)
  const firstHash = useRef(anchorOf(hash))

  const go = useCallback(
    (id: string) => {
      revealTarget(id, true, root.current)
      navigate(`#${id}`, { replace: true })
    },
    [navigate],
  )

  // The address names a part of the plan: it opens, and on arrival it is scrolled to.
  useEffect(() => {
    const id = anchorOf(hash)
    if (!id) return
    if (firstHash.current === id) {
      firstHash.current = null
      revealTarget(id, false, root.current)
    } else {
      openTarget(id, root.current)
    }
  }, [hash])

  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as Element).closest?.('a[href]')
    if (!anchor || !root.current?.contains(anchor)) return
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const id = anchorOf(anchor.getAttribute('href'))
    if (id) {
      event.preventDefault()
      go(id)
    }
  }

  if (model.diagnostics.length > 0) {
    return (
      <PlanKeysProvider value={keys}>
        <div ref={root} className="markdown-body pv-body pv-fallback" data-plan={planId} onClick={onClick}>
          <div className="pv-notice" role="note">
            <p>{w.fallback}</p>
            <ul>
              {model.diagnostics.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </ul>
          </div>
          {model.fallback && <PlanHtml as="div" content={model.fallback} mode="chip" />}
        </div>
      </PlanKeysProvider>
    )
  }

  return (
    <PlanKeysProvider value={keys}>
      <div ref={root} className="markdown-body pv-body" data-plan={planId} onClick={onClick}>
        <section className="pv-overview" id="pv-overview">
          {model.objective && <PlanHtml as="p" className="pv-objective" content={model.objective} mode="chip" />}
          <Kpis kpis={kpis} issueIds={facts.issues.map((issue) => issue.id)} />
        </section>
        <Progress phases={facts.phases} onGo={go} />
        <section className="pv-phases" id="pv-phases">
          <h2>{model.phasesTitle}</h2>
          {facts.phases.map((phase) => (
            <PhaseCard key={phase.phase.id} facts={phase} open={phase.open || firstHash.current === phase.phase.id} onDispatch={props.onDispatch} offline={props.offline} />
          ))}
        </section>
        {facts.elsewhere.length > 0 && <Elsewhere issues={facts.elsewhere} />}
        {model.decisions.length > 0 && (
          <section className="pv-decisions" id="pv-decisions">
            <h2>{w.decisions}</h2>
            <ul>
              {model.decisions.map((decision, index) => (
                <li key={index}>
                  <PlanHtml as="div" content={decision.content} mode="chip" />
                  {decision.note && <span className="pv-when">{decision.note}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
        {model.annex && (
          <section className="pv-annex" id="pv-annex">
            <h2>{w.annex}</h2>
            <p className="pv-dim">{w.annexNote}</p>
            <PlanHtml as="div" className="pv-annex-body" content={model.annex} mode="plain" />
          </section>
        )}
        {model.notes.length > 0 && (
          <aside className="pv-notes" aria-label={w.notes}>
            <ul>
              {model.notes.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </PlanKeysProvider>
  )
}

// ---------------------------------------------------------------- the numbers

function Kpis({ kpis, issueIds }: { kpis: PlanKpis; issueIds: readonly string[] }) {
  const work = useIssueWork()
  const tokens = sumTokens(work, issueIds)
  return (
    <div className="pv-kpis">
      <div className="pv-kpi pv-kpi-lead">
        <span className="pv-kpi-label">{w.landed}</span>
        <span className="pv-kpi-value">{kpis.landedPercent}%</span>
        <span className="pv-kpi-sub">{w.landedSub(kpis.landed, kpis.total)}</span>
        <StageMeter counts={kpis.counts} />
      </div>
      <div className="pv-kpi">
        <span className="pv-kpi-label">{w.phasesExited}</span>
        <span className="pv-kpi-value">{w.phasesExitedValue(kpis.exited, kpis.phaseCount)}</span>
        <span className="pv-kpi-sub">{w.phasesExitedSub}</span>
      </div>
      <div className="pv-kpi">
        <span className="pv-kpi-label">{w.inFlight}</span>
        <span className="pv-kpi-value">{kpis.inFlight}</span>
      </div>
      <div className="pv-kpi">
        <span className="pv-kpi-label">{w.tokens}</span>
        <span className="pv-kpi-value">
          {tokens.tokens !== null ? <span title={w.tokensTitle(tokens.tokens)}>{compact(tokens.tokens)}</span> : w.none}
        </span>
        {tokens.tokens !== null && <span className="pv-kpi-sub">{w.tokensSub(tokens.measured, tokens.worked)}</span>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- listed elsewhere

function Elsewhere({ issues }: { issues: readonly Issue[] }) {
  return (
    <section className="pv-elsewhere" id="pv-elsewhere">
      <h2>{w.elsewhere}</h2>
      <p className="pv-dim">{w.elsewhereNote}</p>
      <ul>
        {issues.map((issue) => (
          <li key={issue.id}>
            <Link className="sun-ref" to={issuePath(issue)}>
              {issue.key}
            </Link>
            <span className="pv-keystate">
              <StateIcon state={issue.state} size={12} />
              {stateName(issue.state)}
            </span>{' '}
            <span className="pv-title">{issue.title}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}
